'use client';

import { useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import {
    PARRAFO_AUTORIZACION,
    PARRAFO_FINALIDAD,
    PARRAFO_PROTECCION_DATOS,
    SALUDO_AUTORIZACION,
    TITULO_AUTORIZACION,
} from '@/content/autorizacionPaciente';

// Alto que ocupa «Información básica sobre protección de datos» con sus seis filas, y hueco que se deja al pie.
const ALTO_BLOQUE_PROTECCION_MM = 70;
const MARGEN_PIE_MM = 22;

export default function ConsentimientoClient() {
    const [formData, setFormData] = useState({
        nombreProfesional: '',
        email: '',
        urlEncargados: '',
    });

    const [documentoGenerado, setDocumentoGenerado] = useState(false);
    const [copiado, setCopiado] = useState(false);
    const previewRef = useRef<HTMLDivElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const generarDocumento = (e: React.FormEvent) => {
        e.preventDefault();
        setDocumentoGenerado(true);
    };

    const generarPDF = async () => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        const contentWidth = pageWidth - margin * 2;
        let yPos = 20;

        // Cargar y añadir el logo
        try {
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';

            await new Promise<void>((resolve, reject) => {
                logoImg.onload = () => resolve();
                logoImg.onerror = reject;
                logoImg.src = '/THERAPY(2).svg';
            });

            // Crear canvas para convertir SVG a imagen
            const canvas = document.createElement('canvas');
            canvas.width = 600;
            canvas.height = 126;
            const ctx = canvas.getContext('2d');

            if (ctx) {
                ctx.fillStyle = '#1F1F1F';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(logoImg, 0, 0, 600, 126);
                const logoDataUrl = canvas.toDataURL('image/png');

                // Añadir logo centrado
                const logoWidth = 60;
                const logoHeight = 12.6;
                const logoX = (pageWidth - logoWidth) / 2;
                doc.addImage(logoDataUrl, 'PNG', logoX, yPos, logoWidth, logoHeight);
                yPos += logoHeight + 15;
            }
        } catch {
            // Si falla el logo, simplemente añadir texto
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text('TherapyRecap', pageWidth / 2, yPos + 10, { align: 'center' });
            yPos += 25;
        }

        // Título
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(TITULO_AUTORIZACION, pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;

        // Contenido principal
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');

        const parrafo1 = SALUDO_AUTORIZACION;
        doc.text(parrafo1, margin, yPos);
        yPos += 10;

        const parrafo2 = doc.splitTextToSize(PARRAFO_FINALIDAD, contentWidth);
        doc.text(parrafo2, margin, yPos);
        yPos += parrafo2.length * 5 + 8;

        const parrafo3 = doc.splitTextToSize(PARRAFO_PROTECCION_DATOS, contentWidth);
        doc.text(parrafo3, margin, yPos);
        yPos += parrafo3.length * 5 + 8;

        const parrafo4 = doc.splitTextToSize(PARRAFO_AUTORIZACION, contentWidth);
        doc.text(parrafo4, margin, yPos);
        yPos += parrafo4.length * 5 + 15;

        // Línea separadora
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        // Campos de firma
        doc.setFont('helvetica', 'bold');
        doc.text('Nombre y apellidos:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.line(margin + 45, yPos, pageWidth - margin, yPos);
        yPos += 12;

        doc.setFont('helvetica', 'bold');
        doc.text('DNI:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.line(margin + 15, yPos, margin + 70, yPos);

        doc.setFont('helvetica', 'bold');
        doc.text('Fecha:', margin + 85, yPos);
        doc.setFont('helvetica', 'normal');
        doc.line(margin + 100, yPos, pageWidth - margin, yPos);
        yPos += 15;

        doc.setFont('helvetica', 'bold');
        doc.text('Firma:', margin, yPos);
        yPos += 25;

        // 17-30: el bloque de protección de datos no cabía debajo de la firma; se salía de la hoja y el pie tapaba
        // «Destinatarios». Si no cabe entero, pasa a una segunda página.
        if (yPos + ALTO_BLOQUE_PROTECCION_MM > doc.internal.pageSize.getHeight() - MARGEN_PIE_MM) {
            doc.addPage();
            yPos = margin;
        }

        // Línea separadora
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;

        // Sección de protección de datos
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('INFORMACIÓN BÁSICA SOBRE PROTECCIÓN DE DATOS', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);

        // Responsable
        doc.setFont('helvetica', 'bold');
        doc.text('Responsable:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(formData.nombreProfesional || '[Nombre del profesional]', margin + 30, yPos);
        yPos += 7;

        // Finalidad
        doc.setFont('helvetica', 'bold');
        doc.text('Finalidad:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text('Mejora de la calidad de la terapia', margin + 25, yPos);
        yPos += 7;

        // Legitimación
        doc.setFont('helvetica', 'bold');
        doc.text('Legitimación:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text('Tu consentimiento (Art.6.1.a. y 9.2.a. del RGPD)', margin + 30, yPos);
        yPos += 7;

        // Destinatarios
        doc.setFont('helvetica', 'bold');
        doc.text('Destinatarios:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        const destinatariosTexto = doc.splitTextToSize(
            `No cederemos tus datos a terceros.${formData.urlEncargados ? ` Consulta encargados de tratamiento: ${formData.urlEncargados}` : ''}`,
            contentWidth - 35
        );
        doc.text(destinatariosTexto, margin + 32, yPos);
        yPos += destinatariosTexto.length * 4 + 3;

        // Derechos
        doc.setFont('helvetica', 'bold');
        doc.text('Derechos:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        const derechosTexto = doc.splitTextToSize(
            `Acceso, rectificación, supresión y otros derechos. Contacto: ${formData.email || '[email de contacto]'}`,
            contentWidth - 25
        );
        doc.text(derechosTexto, margin + 25, yPos);
        yPos += derechosTexto.length * 4 + 3;

        // Info adicional
        doc.setFont('helvetica', 'bold');
        doc.text('Información Adicional:', margin, yPos);
        doc.setFont('helvetica', 'normal');
        yPos += 5;
        const infoTexto = doc.splitTextToSize(
            'Consulta nuestra política de privacidad en: https://www.therapyrecap.com/privacidad',
            contentWidth
        );
        doc.text(infoTexto, margin, yPos);

        // Footer, en cada página
        const yPie = doc.internal.pageSize.getHeight() - 15;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        for (let pagina = 1; pagina <= doc.getNumberOfPages(); pagina++) {
            doc.setPage(pagina);
            doc.text('Documento generado con TherapyRecap | www.therapyrecap.com', pageWidth / 2, yPie, { align: 'center' });
        }

        return doc;
    };

    const descargarPDF = async () => {
        const doc = await generarPDF();
        doc.save('autorizacion-therapyrecap.pdf');
    };

    const imprimirPDF = async () => {
        const doc = await generarPDF();
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(pdfUrl, '_blank');
        if (printWindow) {
            printWindow.onload = () => {
                printWindow.print();
            };
        }
    };

    const copiarTexto = () => {
        const texto = `${TITULO_AUTORIZACION}

${SALUDO_AUTORIZACION}

${PARRAFO_FINALIDAD}

${PARRAFO_PROTECCION_DATOS}

${PARRAFO_AUTORIZACION}

Nombre y apellidos: _________________________________________
DNI: _________________ Fecha: _________________
Firma:

INFORMACIÓN BÁSICA SOBRE PROTECCIÓN DE DATOS
Responsable: ${formData.nombreProfesional || '[Nombre del profesional]'}
Finalidad: Mejora de la calidad de la terapia
Legitimación: Tu consentimiento (Art.6.1.a. y 9.2.a. del RGPD)
Destinatarios: No cederemos tus datos a terceros.
Derechos: Contacto: ${formData.email || '[email de contacto]'}
Info: https://www.therapyrecap.com/privacidad`;

        navigator.clipboard.writeText(texto);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    return (
        <main className="flex flex-col gap-16 md:gap-24 pt-24 pb-16 relative">
            <div className="px-4 md:px-10 lg:px-20 xl:px-40 flex flex-1 justify-center py-5 relative z-10">
                <div className="layout-content-container flex flex-col max-w-4xl flex-1 gap-12">

                    {/* Header */}
                    <div className="flex flex-col gap-4 text-center">
                        <div className="inline-block p-3 rounded-full bg-accent-blue/30 border-2 border-ink shadow-[4px_4px_0px_0px_var(--shadow-color)] mx-auto mb-4 w-fit">
                            <span className="material-symbols-outlined text-4xl text-ink" aria-hidden="true">description</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-ink tracking-tight font-display">
                            Autorización para Uso de TherapyRecap
                        </h1>
                        <p className="text-lg text-ink/80 max-w-2xl mx-auto font-medium">
                            Genera el documento de autorización que tus pacientes deben firmar para el uso de IA en sus sesiones de terapia.
                        </p>
                    </div>

                    {/* Info Card */}
                    <div className="bg-surface-2 border-2 border-ink rounded-3xl p-8 shadow-[4px_4px_0px_0px_var(--shadow-color)]">
                        <div className="flex gap-4">
                            <div className="w-12 h-12 bg-accent-blue text-on-accent-blue rounded-xl flex items-center justify-center shrink-0 border-2 border-ink shadow-[2px_2px_0px_0px_var(--shadow-color)]">
                                <span className="material-symbols-outlined text-ink text-2xl" aria-hidden="true">info</span>
                            </div>
                            <div className="text-ink/80 pt-1">
                                <p className="font-bold text-ink text-lg mb-2 font-display">¿Por qué necesitas este documento?</p>
                                <p className="font-medium">
                                    Según el RGPD y la LOPDGDD, necesitas el consentimiento explícito de tus pacientes para procesar sus datos
                                    de salud mediante herramientas de IA. Este documento cumple con los requisitos legales en España.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Formulario */}
                    <div className="bg-surface-2 p-8 md:p-10 rounded-3xl border-2 border-ink shadow-[8px_8px_0px_0px_var(--shadow-color)]">
                        <h2 className="text-2xl font-bold text-ink mb-8 flex items-center gap-3 font-display border-b-2 border-ink pb-4">
                            <span className="material-symbols-outlined" aria-hidden="true">edit_document</span>
                            Personaliza tu documento
                        </h2>
                        <form onSubmit={generarDocumento} className="flex flex-col gap-6">

                            {/* Nombre del Profesional */}
                            <div className="flex flex-col gap-2">
                                <label htmlFor="nombreProfesional" className="text-sm font-bold text-ink uppercase tracking-wider">
                                    Nombre del Profesional (Responsable)
                                </label>
                                <input
                                    type="text"
                                    id="nombreProfesional"
                                    name="nombreProfesional"
                                    value={formData.nombreProfesional}
                                    onChange={handleChange}
                                    placeholder="Ej. Juan Pérez García"
                                    required
                                    className="w-full px-4 py-3 bg-surface-2 border-2 border-ink rounded-xl text-ink placeholder-ink/40 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_var(--shadow-color)] transition-all font-medium"
                                />
                                <p className="text-xs text-ink/60 font-medium">Este nombre aparecerá como responsable del tratamiento de datos.</p>
                            </div>

                            {/* Email */}
                            <div className="flex flex-col gap-2">
                                <label htmlFor="email" className="text-sm font-bold text-ink uppercase tracking-wider">
                                    Email de contacto para ejercer derechos
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="protecciondatos@tuconsulta.com"
                                    required
                                    className="w-full px-4 py-3 bg-surface-2 border-2 border-ink rounded-xl text-ink placeholder-ink/40 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_var(--shadow-color)] transition-all font-medium"
                                />
                                <p className="text-xs text-ink/60 font-medium">Email donde los pacientes pueden ejercer sus derechos ARCO.</p>
                            </div>

                            {/* URL Encargados (opcional) */}
                            <div className="flex flex-col gap-2">
                                <label htmlFor="urlEncargados" className="text-sm font-bold text-ink uppercase tracking-wider">
                                    URL listado de encargados de tratamiento (opcional)
                                </label>
                                <input
                                    type="url"
                                    id="urlEncargados"
                                    name="urlEncargados"
                                    value={formData.urlEncargados}
                                    onChange={handleChange}
                                    placeholder="https://tuconsulta.com/encargados-tratamiento"
                                    className="w-full px-4 py-3 bg-surface-2 border-2 border-ink rounded-xl text-ink placeholder-ink/40 focus:outline-none focus:ring-0 focus:shadow-[4px_4px_0px_0px_var(--shadow-color)] transition-all font-medium"
                                />
                                <p className="text-xs text-ink/60 font-medium">Si tienes una página con el listado de encargados de tratamiento, inclúyela aquí.</p>
                            </div>

                            {/* Botón */}
                            <button
                                type="submit"
                                className="w-full mt-6 px-6 py-4 bg-ink text-canvas font-bold rounded-xl border-2 border-ink shadow-[4px_4px_0px_0px_#AEC6FF] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_#AEC6FF] transition-all flex items-center justify-center gap-2 text-lg"
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">description</span>
                                Generar Documento
                            </button>
                        </form>
                    </div>

                    {/* Documento Generado */}
                    {documentoGenerado && (
                        <div className="bg-surface-2 p-8 md:p-10 rounded-3xl border-2 border-ink shadow-[8px_8px_0px_0px_var(--shadow-color)]">
                            <div className="flex flex-col xl:flex-row items-center justify-between gap-6 mb-8 border-b-2 border-ink pb-6">
                                <h2 className="text-2xl font-bold text-ink flex items-center gap-3 font-display">
                                    <span className="material-symbols-outlined text-on-tint-success text-3xl" aria-hidden="true">check_circle</span>
                                    Documento Listo
                                </h2>
                                <div className="flex flex-wrap gap-3 w-full xl:w-auto justify-center">
                                    <button
                                        onClick={copiarTexto}
                                        className="px-4 py-2 bg-surface-2 text-ink border-2 border-ink shadow-[2px_2px_0px_0px_var(--shadow-color)] hover:shadow-[1px_1px_0px_0px_var(--shadow-color)] hover:translate-y-[1px] rounded-lg transition-all flex items-center gap-2 text-sm font-bold"
                                    >
                                        <span className="material-symbols-outlined text-lg" aria-hidden="true">
                                            {copiado ? 'check' : 'content_copy'}
                                        </span>
                                        {copiado ? '¡Copiado!' : 'Copiar texto'}
                                    </button>
                                    <button
                                        onClick={imprimirPDF}
                                        className="px-4 py-2 bg-surface-2 text-ink border-2 border-ink shadow-[2px_2px_0px_0px_var(--shadow-color)] hover:shadow-[1px_1px_0px_0px_var(--shadow-color)] hover:translate-y-[1px] rounded-lg transition-all flex items-center gap-2 text-sm font-bold"
                                    >
                                        <span className="material-symbols-outlined text-lg" aria-hidden="true">print</span>
                                        Imprimir
                                    </button>
                                    <button
                                        onClick={descargarPDF}
                                        className="px-4 py-2 bg-ink text-canvas border-2 border-ink shadow-[2px_2px_0px_0px_var(--shadow-color)] hover:shadow-[1px_1px_0px_0px_var(--shadow-color)] hover:translate-y-[1px] rounded-lg transition-all flex items-center gap-2 text-sm font-bold"
                                    >
                                        <span className="material-symbols-outlined text-lg" aria-hidden="true">download</span>
                                        Descargar PDF
                                    </button>
                                </div>
                            </div>

                            {/* Vista previa del documento */}
                            <div ref={previewRef} className="bg-surface-2 text-black p-8 rounded-xl border-2 border-ink/20 shadow-sm max-w-2xl mx-auto">
                                {/* Logo */}
                                <div className="flex justify-center mb-6">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src="/THERAPY(2).svg"
                                        alt="TherapyRecap"
                                        className="h-12 bg-ink px-4 py-2 rounded dark:invert"
                                    />
                                </div>

                                <h3 className="text-xl font-bold text-center mb-6 font-display uppercase tracking-tight">
                                    {TITULO_AUTORIZACION}
                                </h3>

                                <p className="mb-4 font-bold">{SALUDO_AUTORIZACION}</p>

                                <p className="mb-4 text-sm leading-relaxed font-serif text-justify">
                                    {PARRAFO_FINALIDAD}
                                </p>

                                <p className="mb-4 text-sm leading-relaxed font-serif text-justify">
                                    {PARRAFO_PROTECCION_DATOS}
                                </p>

                                <p className="mb-6 text-sm leading-relaxed font-serif text-justify">
                                    {PARRAFO_AUTORIZACION}
                                </p>

                                <hr className="my-6 border-ink/20 dashed" />

                                <div className="space-y-6 text-sm font-mono">
                                    <div className="flex items-end gap-2">
                                        <span className="font-bold whitespace-nowrap">Nombre y apellidos:</span>
                                        <div className="flex-1 border-b-2 border-ink/20 border-dotted h-4"></div>
                                    </div>
                                    <div className="flex flex-wrap gap-8">
                                        <div className="flex flex-1 items-end gap-2">
                                            <span className="font-bold">DNI:</span>
                                            <div className="flex-1 border-b-2 border-ink/20 border-dotted h-4"></div>
                                        </div>
                                        <div className="flex flex-1 items-end gap-2">
                                            <span className="font-bold">Fecha:</span>
                                            <div className="flex-1 border-b-2 border-ink/20 border-dotted h-4"></div>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="font-bold block mb-12">Firma:</span>
                                        <div className="border-b-2 border-ink/20 border-dotted"></div>
                                    </div>
                                </div>

                                <hr className="my-8 border-ink/20 dashed" />

                                <div className="text-xs space-y-2 bg-tint-neutral p-4 rounded border border-gray-200">
                                    <h4 className="font-bold text-sm mb-3">INFORMACIÓN BÁSICA SOBRE PROTECCIÓN DE DATOS</h4>
                                    <div className="grid grid-cols-[100px_1fr] gap-1">
                                        <span className="font-bold">Responsable:</span> <span>{formData.nombreProfesional || '[Nombre del profesional]'}</span>
                                        <span className="font-bold">Finalidad:</span> <span>Mejora de la calidad de la terapia</span>
                                        <span className="font-bold">Legitimación:</span> <span>Tu consentimiento (Art.6.1.a. y 9.2.a. del RGPD)</span>
                                        <span className="font-bold">Destinatarios:</span> <span>No cederemos tus datos a terceros.</span>
                                        <span className="font-bold">Derechos:</span> <span>Acceso, rectificación, supresión. Contacto: {formData.email || '[email de contacto]'}</span>
                                        <span className="font-bold">Info:</span> <span>https://www.therapyrecap.com/privacidad</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CTA */}
                    <div className="text-center py-10 px-6 bg-accent-blue/10 rounded-3xl border-2 border-ink border-dashed">
                        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-4 font-display">
                            ¿Aún no usas TherapyRecap?
                        </h2>
                        <p className="text-ink/80 mb-6 max-w-xl mx-auto font-medium">
                            Transcribe tus sesiones automáticamente, genera notas clínicas con IA y dedica más tiempo a tus pacientes.
                        </p>
                        <a
                            href="/app/register"
                            className="inline-block px-8 py-4 rounded-xl bg-ink text-canvas font-bold text-lg border-2 border-ink shadow-[4px_4px_0px_0px_#AEC6FF] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_#AEC6FF] transition-all"
                        >
                            Prueba Gratis 7 Días
                        </a>
                    </div>

                </div>
            </div>
        </main>
    );
}
